import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
// import { useIsMobile } from '../hooks/useIsMobile';
import { useDispatch, useSelector } from 'react-redux';
import { selectScale, selectPosition } from '../store/selectors/canvasSelectors';
import {setPosition, setScale, setCanvasState} from "../store/slices/canvasSlice";
import { PersistentManager } from "../utils/PersistentManager";

interface ZoomableCanvasProps {
  children: React.ReactNode;
}

const ZoomableCanvas: React.FC<ZoomableCanvasProps> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const initialFitPerformed = useRef<boolean>(false);
  const dispatch = useDispatch();
  
  // Get state from Redux
  const scale = useSelector(selectScale);
  const position = useSelector(selectPosition);
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
  const [lastDistance, setLastDistance] = useState<number | null>(null);
  const [initialScale, setInitialScale] = useState<number>(1);
  // const [pinchCenter, setPinchCenter] = useState({ x: 0, y: 0 });
  // const isMobile = useIsMobile();
  const [isPinching, setIsPinching] = useState(false);
  console.log(`*** scale: ${scale}. isPinching: ${isPinching}`);

  // Load saved state when component mounts
  useEffect(() => {
    const loadSavedCanvasState = async () => {
      try {
        const savedState = await PersistentManager.getInstance().getItem('canvas_state');
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          dispatch(setCanvasState(parsedState));
          
          // Always allow re-fitting if the saved state seems to have invalid dimensions
          // Check if the saved scale is reasonable (not too small which might indicate fitting issues)
          const savedScale = parsedState.scale;
          if (savedScale && savedScale > 0.1) {
            initialFitPerformed.current = true;
          }
          // If scale is too small or invalid, allow re-fitting
        }
      } catch (error) {
        console.error('Failed to load canvas state:', error);
      }
    };
    
    loadSavedCanvasState();
  }, [dispatch]);

  // Handle mouse wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.min(Math.max(scale * delta, 0.5), 5);
    dispatch(setScale(newScale));
  };

  // Handle mouse drag with absolute positioning
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialPosition({ x: position.x, y: position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    const newPosition = {
      x: initialPosition.x + deltaX,
      y: initialPosition.y + deltaY
    };
    
    dispatch(setPosition(newPosition));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Calculate distance between two touch points
  const getDistance = (touch1: React.Touch, touch2: React.Touch): number => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Handle touch events - improved pinch/zoom
  const handleTouchStart = (e: React.TouchEvent) => {
    console.log('Touch start:', e.touches.length);
    
    if (e.touches.length === 1) {
      // Single touch for panning
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX, y: touch.clientY });
      setInitialPosition({ x: position.x, y: position.y });
      setLastDistance(null);
      setIsPinching(false);
      
      // Don't prevent default for single touch to allow SVG interactions
    } else if (e.touches.length === 2) {
      // Two fingers - start pinch zoom
      e.preventDefault(); // Prevent default zoom behavior
      
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const dist = getDistance(touch1, touch2);
      
      // Calculate center point
      const centerX = (touch1.clientX + touch2.clientX) / 2;
      const centerY = (touch1.clientY + touch2.clientY) / 2;
      
      setLastDistance(dist);
      setInitialScale(scale);
      // setPinchCenter({ x: centerX, y: centerY });
      setIsDragging(false);
      setIsPinching(true);
      
      console.log('Pinch zoom started:', { dist, centerX, centerY, scale });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && isDragging && !isPinching) {
      // Single touch panning
      const touch = e.touches[0];
      const deltaX = touch.clientX - dragStart.x;
      const deltaY = touch.clientY - dragStart.y;
      
      const newPosition = {
        x: initialPosition.x + deltaX,
        y: initialPosition.y + deltaY
      };
      
      dispatch(setPosition(newPosition));
    } else if (e.touches.length === 2 && lastDistance !== null && isPinching) {
      // Two finger pinch zoom
      e.preventDefault(); // Prevent default zoom behavior
      
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const newDistance = getDistance(touch1, touch2);
      const ratio = newDistance / lastDistance;
      
      // Calculate new scale
      const newScale = Math.min(Math.max(initialScale * ratio, 0.1), 5);
      
      // Calculate current center point
      const currentCenterX = (touch1.clientX + touch2.clientX) / 2;
      const currentCenterY = (touch1.clientY + touch2.clientY) / 2;
      
      // Get container rect for relative positioning
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;
      
      // Calculate the position relative to container
      const relativeX = currentCenterX - containerRect.left;
      const relativeY = currentCenterY - containerRect.top;
      
      // Calculate zoom point in content coordinates
      const contentX = (relativeX - position.x) / scale;
      const contentY = (relativeY - position.y) / scale;
      
      // Calculate new position to keep zoom point at same screen location
      const newPosition = {
        x: relativeX - contentX * newScale,
        y: relativeY - contentY * newScale
      };
      
      dispatch(setScale(newScale));
      dispatch(setPosition(newPosition));
      
      console.log('Pinch zoom:', { ratio, newScale, newPosition });
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    console.log('Touch end:', e.touches.length);
    
    if (e.touches.length < 2) {
      setLastDistance(null);
      setIsPinching(false);
    }
    
    if (e.touches.length === 0) {
      setIsDragging(false);
    }
  };

  // Function to fit content to container - enhanced for all screen sizes
  const fitContent = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    
    if (!container || !content) {
      console.warn('Container or content not available for fitting');
      return;
    }
    
    const svg = content.querySelector('svg');
    if (!svg) {
      console.warn('SVG element not found for fitting');
      return;
    }
    
    // Get container dimensions with multiple attempts
    let containerRect = container.getBoundingClientRect();
    
    // If container dimensions are invalid, wait and try again
    if (containerRect.width <= 0 || containerRect.height <= 0) {
      console.warn('Container dimensions not ready, retrying...', containerRect);
      setTimeout(() => {
        if (!initialFitPerformed.current) {
          fitContent();
        }
      }, 100);
      return;
    }
    
    // Get SVG dimensions using multiple approaches for reliability
    let svgWidth, svgHeight;
    
    // Method 1: Try to get SVG dimensions from viewBox first (most reliable)
    const viewBox = svg.getAttribute('viewBox');
    if (viewBox) {
      const viewBoxParts = viewBox.split(/[\s,]+/).map(Number);
      if (viewBoxParts.length >= 4) {
        svgWidth = viewBoxParts[2];
        svgHeight = viewBoxParts[3];
      }
    }
    
    // Method 2: Fallback to width/height attributes
    if (!svgWidth || !svgHeight) {
      const widthAttr = svg.getAttribute('width');
      const heightAttr = svg.getAttribute('height');
      if (widthAttr && heightAttr) {
        svgWidth = parseFloat(widthAttr);
        svgHeight = parseFloat(heightAttr);
      }
    }
    
    // Method 3: Fallback to getBoundingClientRect and other properties
    if (!svgWidth || !svgHeight) {
      const svgRect = svg.getBoundingClientRect();
      svgWidth = svgRect.width || svg.clientWidth || (svg as any).offsetWidth || 100;
      svgHeight = svgRect.height || svg.clientHeight || (svg as any).offsetHeight || 100;
    }
    
    // Ensure we have valid dimensions with reasonable defaults
    if (!svgWidth || !svgHeight || svgWidth <= 0 || svgHeight <= 0) {
      console.warn('Invalid SVG dimensions, using defaults:', { svgWidth, svgHeight });
      svgWidth = svgWidth || 100;
      svgHeight = svgHeight || 100;
    }
    
    // Calculate scale to fit content with some padding
    const paddingFactor = 0.9; // 90% of available space to leave some margin
    const scaleX = (containerRect.width * paddingFactor) / svgWidth;
    const scaleY = (containerRect.height * paddingFactor) / svgHeight;
    const newScale = Math.min(scaleX, scaleY);
    
    // Ensure scale is reasonable
    const clampedScale = Math.max(0.1, Math.min(newScale, 5));
    
    // // Center the content
    // const scaledWidth = svgWidth * clampedScale;
    // const scaledHeight = svgHeight * clampedScale;
    const newPosition = {
      x: 0, // (containerRect.width - scaledWidth) / 2,
      y: 0, // (containerRect.height - scaledHeight) / 2
    };
    
    console.log('Fitting content to screen:', { 
      containerRect: { width: containerRect.width, height: containerRect.height },
      svgDimensions: { width: svgWidth, height: svgHeight },
      calculatedScale: newScale,
      clampedScale,
      newPosition
    });
    
    dispatch(setScale(clampedScale));
    dispatch(setPosition(newPosition));
    initialFitPerformed.current = true;
  }, [dispatch]);

  // Run fit content when needed - enhanced for all devices
  useEffect(() => {
    const svg = contentRef.current?.querySelector('svg');
    
    // Only perform initial fit if:
    // 1. We haven't already performed initial fit
    // 2. We have an SVG element to fit
    if (!initialFitPerformed.current && svg) {
      // Use longer timeout for better reliability across all devices
      const timeout = 300;
      
      // Multiple attempts with retry logic for all devices
      const fitWithRetries = () => {
        const container = containerRef.current;
        if (!container || !svg) return;
        
        const containerRect = container.getBoundingClientRect();
        const hasValidDimensions = containerRect.width > 0 && containerRect.height > 0;
        
        if (hasValidDimensions) {
          fitContent();
        } else {
          // Retry after a longer delay if dimensions aren't ready
          setTimeout(fitContent, 500);
        }
      };
      
      const timer = setTimeout(fitWithRetries, timeout);
      return () => clearTimeout(timer);
    }
  }, [fitContent]);

  // Additional effect to ensure fitting after images load - for all devices
  useEffect(() => {
    if (initialFitPerformed.current) return;
    
    const svg = contentRef.current?.querySelector('svg');
    if (!svg) return;
    
    // Listen for SVG load events on all devices
    const handleSvgLoad = () => {
      if (!initialFitPerformed.current) {
        setTimeout(fitContent, 200);
      }
    };
    
    // Check if images within SVG are loaded
    const images = svg.querySelectorAll('image');
    if (images.length > 0) {
      let loadedCount = 0;
      images.forEach(img => {
        if ((img as any).complete) {
          loadedCount++;
        } else {
          img.addEventListener('load', () => {
            loadedCount++;
            if (loadedCount === images.length) {
              handleSvgLoad();
            }
          });
        }
      });
      
      if (loadedCount === images.length) {
        handleSvgLoad();
      }
    } else {
      // No images, just wait for general render - applies to all devices
      setTimeout(() => {
        if (!initialFitPerformed.current) {
          fitContent();
        }
      }, 400);
    }
  }, [fitContent]);

  // Handle window resize events with debouncing
  useEffect(() => {
    let resizeTimer: NodeJS.Timeout;
    
    const handleResize = () => {
      // Debounce resize events to avoid excessive calculations
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // Reset the initial fit flag to allow re-fitting on resize
        initialFitPerformed.current = false;
        fitContent();
      }, 150);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, [fitContent]);

  return (
    <Container 
      ref={containerRef}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        position: 'relative',
        touchAction: 'none'
      }}
    >
      <Content
        ref={contentRef}
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          transformOrigin: 'top left'
        }}
      >
        {children}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  user-select: none;
  touch-action: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
`;

const Content = styled.div`
  transform-origin: center;
  will-change: transform;
`;

export default ZoomableCanvas;
